/**
 * ScopeFilterBuilder (#1867 PR2a) — the visual condition builder for the
 * app's issue-source scope filter. Field → operator → value(s), chained by a
 * Match ALL / ANY selector; an Advanced (raw JQL) escape; a live approximate
 * match count. It owns a `ScopeFilterModel` internally (seeded once from the
 * stored `filterJql`) and emits the compiled fragment via `onChange`. All
 * compile/parse goes through `@/lib/scopeFilter`, so the builder and the
 * Advanced view are two views of one string — and JQL the flat builder can't
 * model stays in Advanced, never silently rewritten.
 *
 * Fields: workflow stage (fixed), fix version + priority (discovered lists),
 * labels + assignee/reporter (type-ahead), created/updated (relative or
 * absolute date). The value editor is chosen by the field's value kind.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { Label, SearchableSelect } from '@/components/ui'
import { applicationsAPI } from '@/lib/api'
import {
  compile,
  parse,
  isConditionComplete,
  fieldConfig,
  FIELDS,
  operatorsForField,
  STATUS_CATEGORY_VALUES,
  type ScopeFilterModel,
  type Condition,
  type FieldKey,
  type OperatorKey,
} from '@/lib/scopeFilter'

const PREVIEW_DEBOUNCE_MS = 400

/** The default operator for a field — its first (operators are per-field, so
 * switching field must reset to one the new field actually offers). */
function defaultOpFor(field: FieldKey): OperatorKey {
  return operatorsForField(field)[0].key
}

interface Props {
  appId: string
  connectionId: string
  projectKeys: string[]
  /** Mapped requirement + defect type ids — the preview's scope, so "matches
   * N" reflects exactly what will sync. */
  issueTypeIds: string[]
  value: string
  onChange: (filterJql: string) => void
  /** Reports whether the filter is safe to save (no incomplete condition, no
   * server-rejected filter) so the card can gate its Save button. */
  onValidityChange?: (valid: boolean) => void
  /** Seed for the accountId → displayName map (from the binding's cache), so a
   * saved people filter shows names on reload. */
  initialUserNames?: Record<string, string>
  /** Reports the accountId → displayName map for people currently in the
   * filter, so the card can persist it (`userDisplayNames`). */
  onUserNamesChange?: (names: Record<string, string>) => void
}

type PreviewState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ok'; count: number | null }
  | { status: 'error'; message: string }

export function ScopeFilterBuilder({
  appId,
  connectionId,
  projectKeys,
  issueTypeIds,
  value,
  onChange,
  onValidityChange,
  initialUserNames,
  onUserNamesChange,
}: Props) {
  // Seeded ONCE from the stored filter; the builder owns it thereafter. The
  // card remounts this (via a connectionId key) when the connection changes.
  const [model, setModel] = useState<ScopeFilterModel>(() => parse(value))
  const [advancedUnrepresentable, setAdvancedUnrepresentable] = useState(false)

  const [versions, setVersions] = useState<string[] | null>(null)
  const [priorities, setPriorities] = useState<string[] | null>(null)
  const [preview, setPreview] = useState<PreviewState>({ status: 'idle' })
  // accountId → displayName for people in the filter (seeded from the binding,
  // grown from user-search selections).
  const [nameMap, setNameMap] = useState<Record<string, string>>(() => initialUserNames ?? {})

  const filterJql = useMemo(() => compile(model), [model])
  const projectsKey = projectKeys.join(',')
  const typesKey = issueTypeIds.join(',')

  // Emit the compiled fragment whenever it changes.
  useEffect(() => {
    onChange(filterJql)
  }, [filterJql]) // eslint-disable-line react-hooks/exhaustive-deps

  const complete = model.mode === 'advanced' ? true : model.conditions.every(isConditionComplete)

  useEffect(() => {
    onValidityChange?.(complete && preview.status !== 'error')
  }, [complete, preview.status]) // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced, cancel-superseded preview.
  const reqIdRef = useRef(0)
  useEffect(() => {
    if (!complete || projectKeys.length === 0) {
      setPreview({ status: 'idle' })
      return
    }
    const id = ++reqIdRef.current
    setPreview({ status: 'loading' })
    const timer = setTimeout(() => {
      applicationsAPI
        .previewIssueSourceFilter(appId, { connectionId, projectKeys, issueTypeIds, filterJql })
        .then(({ count }) => {
          if (id === reqIdRef.current) setPreview({ status: 'ok', count })
        })
        .catch((err) => {
          if (id === reqIdRef.current) {
            setPreview({ status: 'error', message: err instanceof Error ? err.message : "That filter isn't valid." })
          }
        })
    }, PREVIEW_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [filterJql, complete, appId, connectionId, projectsKey, typesKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // Discovered value lists — loaded lazily when a condition needs them.
  const needsVersions = model.mode === 'builder' && model.conditions.some((c) => c.field === 'fixVersion' && c.op !== 'isEmpty')
  const needsPriorities = model.mode === 'builder' && model.conditions.some((c) => c.field === 'priority' && c.op !== 'isEmpty')
  // Versions are per (connection, projects) — invalidate on a project change.
  useEffect(() => setVersions(null), [projectsKey])
  useEffect(() => {
    if (!needsVersions || versions !== null || projectKeys.length === 0) return
    applicationsAPI
      .getIssueSourceVersions(appId, connectionId, projectKeys)
      .then((vs) => setVersions(vs.map((v) => v.name)))
      .catch(() => setVersions([]))
  }, [needsVersions, versions, appId, connectionId, projectsKey]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!needsPriorities || priorities !== null) return
    applicationsAPI
      .getIssueSourcePriorities(appId, connectionId)
      .then((ps) => setPriorities(ps.map((p) => p.name)))
      .catch(() => setPriorities([]))
  }, [needsPriorities, priorities, appId, connectionId]) // eslint-disable-line react-hooks/exhaustive-deps

  const optionsFor = useCallback(
    (field: FieldKey): { options: string[] | null } => {
      if (field === 'statusCategory') return { options: [...STATUS_CATEGORY_VALUES] }
      if (field === 'fixVersion') return { options: versions }
      return { options: priorities }
    },
    [versions, priorities],
  )

  // Async loaders for the type-ahead fields (labels / people).
  const loadLabels = useCallback(
    (query: string) => applicationsAPI.suggestIssueSourceLabels(appId, connectionId, query).then((ls) => ls.map((l) => ({ value: l, label: l }))),
    [appId, connectionId],
  )
  const loadUsers = useCallback(
    (query: string) => applicationsAPI.searchIssueSourceUsers(appId, connectionId, query).then((us) => us.map((u) => ({ value: u.accountId, label: u.displayName }))),
    [appId, connectionId],
  )
  const recordName = useCallback((accountId: string, name: string) => {
    setNameMap((m) => (m[accountId] === name ? m : { ...m, [accountId]: name }))
  }, [])

  // Report the display names for the people currently in the filter, for save.
  useEffect(() => {
    if (!onUserNamesChange) return
    const used: Record<string, string> = {}
    if (model.mode === 'builder') {
      for (const c of model.conditions) {
        if (fieldConfig(c.field).valueKind !== 'user') continue
        for (const id of c.values) if (nameMap[id]) used[id] = nameMap[id]
      }
    }
    onUserNamesChange(used)
  }, [filterJql, nameMap]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── model mutators (builder mode) ──
  const conditions = model.mode === 'builder' ? model.conditions : []
  const match = model.mode === 'builder' ? model.match : 'all'
  const setConditions = (next: Condition[]) => setModel({ mode: 'builder', match, conditions: next })
  const updateCondition = (i: number, patch: Partial<Condition>) =>
    setConditions(conditions.map((c, idx) => (idx === i ? { ...c, ...patch } : c)))
  const addCondition = () =>
    setConditions([...conditions, { field: 'statusCategory', op: 'isAnyOf', values: [] }])
  const removeCondition = (i: number) => setConditions(conditions.filter((_, idx) => idx !== i))

  // Pick the value editor by the field's value kind (isEmpty needs none).
  const renderValueEditor = (c: Condition, i: number): ReactNode => {
    if (c.op === 'isEmpty') return null
    const setValues = (values: string[]) => updateCondition(i, { values })
    const kind = fieldConfig(c.field).valueKind
    if (kind === 'date') {
      return <RelativeOrDateInput op={c.op} value={c.values[0] ?? ''} onChange={(v) => setValues(v ? [v] : [])} />
    }
    if (kind === 'labels') {
      return <AsyncMultiValuePicker selected={c.values} onChange={setValues} loadOptions={loadLabels} renderLabel={(v) => v} allowFreeText placeholder="+ label" />
    }
    if (kind === 'user') {
      return (
        <AsyncMultiValuePicker
          selected={c.values}
          onChange={setValues}
          loadOptions={loadUsers}
          onSelectOption={(o) => recordName(o.value, o.label)}
          renderLabel={(v) => nameMap[v] ?? 'Unknown user'}
          placeholder="+ person"
        />
      )
    }
    return <MultiValuePicker selected={c.values} onChange={setValues} options={optionsFor(c.field).options} label={`${fieldConfig(c.field).label} values`} />
  }

  const toAdvanced = () => setModel({ mode: 'advanced', jql: compile(model) })
  const tryBackToBuilder = () => {
    const parsed = parse(model.mode === 'advanced' ? model.jql : filterJql)
    if (parsed.mode === 'builder') {
      setAdvancedUnrepresentable(false)
      setModel(parsed)
    } else {
      setAdvancedUnrepresentable(true) // can't show as a visual rule — stay in Advanced
    }
  }

  return (
    <div className="space-y-2">
      <Label>Which issues should be in play?</Label>

      {model.mode === 'advanced' ? (
        <div className="space-y-1.5">
          <textarea
            value={model.jql}
            onChange={(e) => {
              setAdvancedUnrepresentable(false)
              setModel({ mode: 'advanced', jql: e.target.value })
            }}
            rows={2}
            placeholder="Leave empty to include every mapped type in the projects above."
            className="w-full rounded-md border border-border bg-background px-2.5 py-2 font-mono text-xs"
            aria-label="Scope filter (JQL)"
          />
          {advancedUnrepresentable && (
            <p className="text-[11px] text-muted-foreground">This filter is edited as JQL — it can’t be shown as a visual rule.</p>
          )}
          <button type="button" onClick={tryBackToBuilder} className="text-xs text-primary hover:underline">
            ← Back to the visual builder
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <span>Match</span>
            <select
              value={match}
              onChange={(e) => setModel({ mode: 'builder', match: e.target.value as 'all' | 'any', conditions })}
              aria-label="Match all or any"
              className="rounded-md border border-border bg-background px-2 py-1 text-xs"
            >
              <option value="all">ALL</option>
              <option value="any">ANY</option>
            </select>
            <span className="text-muted-foreground text-xs">of these conditions</span>
          </div>

          {conditions.map((c, i) => (
            <ConditionRow
              key={i}
              condition={c}
              valueEditor={renderValueEditor(c, i)}
              onField={(field) => updateCondition(i, { field, op: defaultOpFor(field), values: [] })}
              onOp={(op) => updateCondition(i, { op, ...(op === 'isEmpty' ? { values: [] } : {}) })}
              onRemove={() => removeCondition(i)}
            />
          ))}

          <button
            type="button"
            onClick={addCondition}
            className="rounded-md border border-dashed border-primary/50 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/5"
          >
            + Add condition
          </button>

          <button type="button" onClick={toAdvanced} className="block text-xs text-muted-foreground hover:text-foreground">
            Advanced — edit as JQL
          </button>
          {filterJql && (
            <p className="rounded-md bg-muted px-2.5 py-1.5 font-mono text-[11px] text-muted-foreground">
              <span className="font-sans font-medium text-foreground">This rule becomes: </span>
              {filterJql}
            </p>
          )}
        </div>
      )}

      <PreviewLine preview={preview} incomplete={!complete} />
      <p className="text-[11px] text-muted-foreground">
        Leave empty to include every mapped type in the projects above. This same filter drives the issue list, import, and automatic sync.
      </p>
    </div>
  )
}

// ── one condition row ──
function ConditionRow({
  condition,
  valueEditor,
  onField,
  onOp,
  onRemove,
}: {
  condition: Condition
  valueEditor: ReactNode
  onField: (f: FieldKey) => void
  onOp: (o: OperatorKey) => void
  onRemove: () => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-background px-2.5 py-2">
      <select
        value={condition.field}
        onChange={(e) => onField(e.target.value as FieldKey)}
        aria-label="Field"
        className="rounded-md border border-border bg-background px-2 py-1 text-xs font-medium"
      >
        {FIELDS.map((f) => (
          <option key={f.key} value={f.key}>{f.label}</option>
        ))}
      </select>
      <select
        value={condition.op}
        onChange={(e) => onOp(e.target.value as OperatorKey)}
        aria-label="Comparison"
        className="rounded-md border border-border bg-background px-2 py-1 text-xs"
      >
        {operatorsForField(condition.field).map((o) => (
          <option key={o.key} value={o.key}>{o.label}</option>
        ))}
      </select>
      {valueEditor}
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove condition"
        className="ml-auto text-muted-foreground hover:text-foreground text-base leading-none px-1"
      >
        ×
      </button>
    </div>
  )
}

// ── selected-value chips (shared by both pickers) ──
function Chips({ selected, renderLabel, onRemove }: { selected: string[]; renderLabel?: (v: string) => string; onRemove: (v: string) => void }) {
  const label = renderLabel ?? ((v: string) => v)
  return (
    <>
      {selected.map((v) => (
        <span key={v} className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[11px] text-primary">
          {label(v)}
          <button type="button" onClick={() => onRemove(v)} aria-label={`Remove ${label(v)}`} className="opacity-70 hover:opacity-100">×</button>
        </span>
      ))}
    </>
  )
}

// ── chips + constrained add-picker ──
function MultiValuePicker({
  selected,
  onChange,
  options,
  label,
}: {
  selected: string[]
  onChange: (v: string[]) => void
  options: string[] | null
  label: string
}) {
  if (options === null) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" /> loading…
      </span>
    )
  }
  const remaining = options.filter((o) => !selected.includes(o))
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Chips selected={selected} onRemove={(v) => onChange(selected.filter((s) => s !== v))} />
      <SearchableSelect
        value=""
        onChange={(v) => { if (v) onChange([...selected, v]) }}
        options={remaining.map((o) => ({ value: o, label: o }))}
        placeholder="+ value"
        searchPlaceholder="Search…"
        aria-label={label}
        disabled={remaining.length === 0}
      />
    </div>
  )
}

// ── async type-ahead multi-value picker (labels + people) ──
interface AsyncOption { value: string; label: string }
function AsyncMultiValuePicker({
  selected,
  onChange,
  loadOptions,
  renderLabel,
  onSelectOption,
  allowFreeText,
  placeholder,
}: {
  selected: string[]
  onChange: (v: string[]) => void
  loadOptions: (query: string) => Promise<AsyncOption[]>
  renderLabel: (value: string) => string
  onSelectOption?: (opt: AsyncOption) => void
  allowFreeText?: boolean
  placeholder?: string
}) {
  const [query, setQuery] = useState('')
  const [options, setOptions] = useState<AsyncOption[]>([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const reqId = useRef(0)

  // Debounced + cancel-superseded search.
  useEffect(() => {
    const q = query.trim()
    if (!q) {
      setOptions([])
      setLoading(false)
      return
    }
    const id = ++reqId.current
    setLoading(true)
    const timer = setTimeout(() => {
      loadOptions(q)
        .then((opts) => {
          if (id === reqId.current) {
            setOptions(opts)
            setLoading(false)
          }
        })
        .catch(() => {
          if (id === reqId.current) {
            setOptions([])
            setLoading(false)
          }
        })
    }, 300)
    return () => clearTimeout(timer)
  }, [query]) // eslint-disable-line react-hooks/exhaustive-deps

  const add = (opt: AsyncOption) => {
    if (!selected.includes(opt.value)) {
      onChange([...selected, opt.value])
      onSelectOption?.(opt)
    }
    setQuery('')
    setOptions([])
    setOpen(false)
  }
  const suggestions = options.filter((o) => !selected.includes(o.value))
  const trimmed = query.trim()
  const showFreeText = !!allowFreeText && trimmed.length > 0 && !suggestions.some((s) => s.value === trimmed)

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Chips selected={selected} renderLabel={renderLabel} onRemove={(v) => onChange(selected.filter((s) => s !== v))} />
      <div className="relative">
        <input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={placeholder ?? '+ value'}
          aria-label={placeholder ?? 'Search'}
          className="w-32 rounded-md border border-border bg-background px-2 py-1 text-xs"
        />
        {open && (loading || suggestions.length > 0 || showFreeText) && (
          <div className="absolute z-10 mt-1 max-h-48 w-56 overflow-auto rounded-md border border-border bg-background shadow-md">
            {loading && <div className="px-2 py-1 text-[11px] text-muted-foreground">Searching…</div>}
            {suggestions.map((o) => (
              <button key={o.value} type="button" onMouseDown={(e) => { e.preventDefault(); add(o) }} className="block w-full truncate px-2 py-1 text-left text-xs hover:bg-muted">
                {o.label}
              </button>
            ))}
            {showFreeText && (
              <button type="button" onMouseDown={(e) => { e.preventDefault(); add({ value: trimmed, label: trimmed }) }} className="block w-full px-2 py-1 text-left text-xs hover:bg-muted">
                Add “{trimmed}”
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ── relative-or-absolute date value editor (created / updated) ──
function RelativeOrDateInput({ op, value, onChange }: { op: OperatorKey; value: string; onChange: (v: string) => void }) {
  if (op === 'inLast') {
    const m = /^-(\d+)([dw])$/.exec(value)
    const n = m ? m[1] : ''
    const unit = m ? m[2] : 'd'
    const emit = (nn: string, uu: string) => onChange(nn && Number(nn) > 0 ? `-${nn}${uu}` : '')
    return (
      <span className="inline-flex items-center gap-1">
        <input
          type="number"
          min={1}
          value={n}
          onChange={(e) => emit(e.target.value, unit)}
          placeholder="30"
          aria-label="Amount"
          className="w-16 rounded-md border border-border bg-background px-2 py-1 text-xs"
        />
        <select value={unit} onChange={(e) => emit(n, e.target.value)} aria-label="Unit" className="rounded-md border border-border bg-background px-2 py-1 text-xs">
          <option value="d">days</option>
          <option value="w">weeks</option>
        </select>
      </span>
    )
  }
  return (
    <input
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label="Date"
      className="rounded-md border border-border bg-background px-2 py-1 text-xs"
    />
  )
}

// ── the live "matches N" line ──
function PreviewLine({ preview, incomplete }: { preview: PreviewState; incomplete: boolean }) {
  if (incomplete) {
    return <p className="text-xs text-amber-700 dark:text-amber-400">Finish each condition (pick a value) to see how many issues match.</p>
  }
  if (preview.status === 'loading') {
    return <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Checking…</p>
  }
  if (preview.status === 'error') {
    return <p className="text-xs text-destructive" role="alert">✕ {preview.message}</p>
  }
  if (preview.status === 'ok') {
    if (preview.count === null) return null
    if (preview.count === 0) {
      return <p className="text-xs text-amber-700 dark:text-amber-400">⚠ This filter matches 0 issues right now.</p>
    }
    return <p className="text-xs text-green-700 dark:text-green-400">✓ About {preview.count.toLocaleString()} {preview.count === 1 ? 'issue' : 'issues'} match right now.</p>
  }
  return null
}
